import type { FC, HTMLAttributes } from 'react';
import { useId } from 'react';

import { useNavCollapsedSections } from '@/hooks/useNavCollapsedSections';
import { NavSection } from '@/lib/base/nav';

import { NavItem } from './NavItem';
import { NavSectionToggle } from './NavSectionToggle';

export interface NavListItem {
  /** Display label. */
  label: string;
  /** URL to navigate to. */
  href?: string;
  /** Icon component to display. */
  icon?: FC<HTMLAttributes<HTMLOrSVGElement>>;
  /** Badge to display. */
  badge?: string | number;
  /** Number that increments when the caller wants to replay the badge pulse animation. */
  pulseKey?: number;
  /** data-component handle for the badge span (e2e). */
  badgeDataComponent?: string;
  /** Keyboard shortcut key. */
  shortcut?: string;
  /** Whether the shortcut badge is always visible (e.g. pipeline shortcuts). */
  alwaysVisible?: boolean;
  /** Whether to render a trailing chevron. */
  showChevron?: boolean;
  /** Whether this item is a section header (non-interactive label). */
  isSectionHeader?: boolean;
  /** Whether this item is a horizontal divider (non-interactive rule). */
  isDivider?: boolean;
  /** Tailwind background color class for pipeline icon (e.g. 'bg-brand-600'). */
  pipelineColorClass?: string;
  /** Child items rendered with indentation. */
  children?: NavListItem[];
  /** When true, renders a trailing lock icon and "Requires a full seat" tooltip. */
  locked?: boolean;
  /** Handlers for prefetching content on user intent. */
  onPrefetch?: {
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    onMouseDown?: () => void;
  };
}

interface NavListProps {
  items: NavListItem[];
  activeUrl: string;
  isModifierHeld: boolean;
  orgId: string;
}

/** Check if the current URL matches a nav item's href, supporting nested pages and query params. */
function isUrlActive(activeUrl: string, href: string): boolean {
  // Strip query params from href for prefix matching
  const hrefPath = href.split('?')[0];
  const activePath = activeUrl.split('?')[0];

  // Org-root paths like "/:orgId" must match exactly — prefix matching
  // would highlight Home for every page under the org.
  const segments = hrefPath.replace(/^\//, '').split('/');
  if (segments.length <= 1) {
    return activePath === hrefPath;
  }

  // Exact match or nested page (e.g. /crm/deals matches /crm/deals/123)
  return activePath === hrefPath || activePath.startsWith(hrefPath + '/');
}

export const NavList = ({
  items,
  activeUrl,
  isModifierHeld,
  orgId,
}: NavListProps) => {
  const { collapsedSections, toggle } = useNavCollapsedSections(orgId);
  const idPrefix = useId();

  return (
    <NavSection>
      {items.map((item, index) => {
        const showShortcut = item.alwaysVisible || isModifierHeld;
        const current = !!item.href && isUrlActive(activeUrl, item.href);

        if (item.isSectionHeader) {
          return (
            // eslint-disable-next-line react/no-array-index-key -- Section headers may share labels; position-based key is appropriate
            <li key={index}>
              <NavItem label={item.label} isSectionHeader />
            </li>
          );
        }

        if (item.isDivider) {
          return (
            // eslint-disable-next-line react/no-array-index-key -- Dividers have no identity; position-based key is appropriate
            <li key={index} role='separator' aria-orientation='horizontal'>
              <div className='my-2 border-t border-secondary' />
            </li>
          );
        }

        if (item.children?.length) {
          const anyChildActive = item.children.some(
            child => !!child.href && isUrlActive(activeUrl, child.href)
          );
          const sectionId = item.href ?? item.label;
          const sectionCollapsed = collapsedSections.has(sectionId);
          const sectionListId = `${idPrefix}section-${index}`;
          return (
            <li key={item.label}>
              <div className='flex items-center gap-1'>
                <div className='min-w-0 flex-1'>
                  <NavItem
                    label={item.label}
                    href={item.href}
                    icon={item.icon}
                    badge={item.badge}
                    pulseKey={item.pulseKey}
                    badgeDataComponent={item.badgeDataComponent}
                    shortcut={item.shortcut}
                    showShortcut={showShortcut}
                    pipelineColorClass={item.pipelineColorClass}
                    current={current || anyChildActive}
                    onPrefetch={item.onPrefetch}
                    locked={item.locked}
                  />
                </div>
                <NavSectionToggle
                  sectionId={sectionId}
                  sectionLabel={item.label}
                  isExpanded={!sectionCollapsed}
                  controlsId={sectionListId}
                  onToggle={toggle}
                />
              </div>
              {!sectionCollapsed && (
                <ul id={sectionListId}>
                  {item.children.map(child => {
                    const childCurrent =
                      !!child.href && isUrlActive(activeUrl, child.href);
                    const childShowShortcut =
                      child.alwaysVisible || isModifierHeld;
                    return (
                      <li key={child.href ?? child.label}>
                        <NavItem
                          label={child.label}
                          href={child.href}
                          icon={child.icon}
                          badge={child.badge}
                          pulseKey={child.pulseKey}
                          badgeDataComponent={child.badgeDataComponent}
                          shortcut={child.shortcut}
                          showShortcut={childShowShortcut}
                          showChevron={child.showChevron}
                          pipelineColorClass={child.pipelineColorClass}
                          isChild
                          current={childCurrent}
                          onPrefetch={child.onPrefetch}
                          locked={child.locked}
                        />
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        }

        return (
          <li key={item.label}>
            <NavItem
              label={item.label}
              href={item.href}
              icon={item.icon}
              badge={item.badge}
              pulseKey={item.pulseKey}
              badgeDataComponent={item.badgeDataComponent}
              shortcut={item.shortcut}
              showShortcut={showShortcut}
              showChevron={item.showChevron}
              pipelineColorClass={item.pipelineColorClass}
              current={current}
              onPrefetch={item.onPrefetch}
              locked={item.locked}
            />
          </li>
        );
      })}
    </NavSection>
  );
};
